/**
 * React-Query-Hook für die App-Konfiguration (`/api/config`).
 *
 * Settings werden 5 min gecacht, da sie sich selten ändern. Wenn der Admin
 * den Wert ändert, invalidiert die Mutation den Cache, sodass UI sofort
 * neu rendert. Default-Fallback: 556 € — falls die Config noch nicht
 * geladen werden konnte (offline, erster Start). Phase P: Wert ist seit
 * Kapitel 20.5 pro Firma editierbar — dieser Fallback ist nur der
 * gesetzliche 2026er-Wert für den Fall "noch keine Antwort vom Server".
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../store/auth';
import { apiFetch } from '../lib/api';

export interface AppConfig {
  minijobLimitEur: number;
  updatedAt: string;
}

/** Lokaler Fallback-Wert, falls Server noch nicht geantwortet hat. */
export const DEFAULT_MINIJOB_LIMIT = 603;

const QK = ['app-config'] as const;

export function useAppConfig() {
  const token = useAuth((s) => s.token);
  return useQuery({
    queryKey: QK,
    enabled: !!token,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const data = await apiFetch<{ config: AppConfig }>('/api/config', { token });
      return data.config;
    },
  });
}

/** Convenience: nur die Minijob-Grenze, mit Fallback wenn noch nichts geladen. */
export function useMinijobLimit(): number {
  const { data } = useAppConfig();
  return data?.minijobLimitEur ?? DEFAULT_MINIJOB_LIMIT;
}

export function useUpdateAppConfig() {
  const token = useAuth((s) => s.token);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<Pick<AppConfig, 'minijobLimitEur'>>) => {
      const data = await apiFetch<{ config: AppConfig }>('/api/config', {
        method: 'PUT',
        body: input,
        token,
      });
      return data.config;
    },
    onSuccess: (cfg) => {
      qc.setQueryData(QK, cfg);
    },
  });
}
